import { appRpc, previewRpc } from "./rpc";
import * as map from "./mapping";
import { accounts, scripts, backtests, upsert } from "./resources";
import type { Account, Script, Backtest } from "./types";
import type { OrderScope } from "./resources";

type Config = Record<string, unknown>;
type ConfigRequest = { category: string; name: string; config?: Config };
type MarketRequest = { market_provider: string; market_config?: Config };
export type PreviewInput = Pick<
  Backtest,
  | "market_provider"
  | "market_config"
  | "strategy_provider"
  | "strategy_config"
  | "engine_provider"
  | "engine_config"
  | "symbol"
  | "timeframe"
  | "start_date"
> &
  Pick<Partial<Backtest>, "end_date" | "bar_magnifier" | "recalculate"> & {
    analyses?: string[];
    recalculate_setting?: string;
  };
export const accountInput = (
  d: Pick<Account, "name" | "description" | "provider" | "config" | "enabled">
) => ({
  name: d.name,
  description: d.description,
  provider: map.provider(d.provider, d.config),
  enabled: d.enabled,
});
export const backtestInput = (
  d: Omit<Backtest, "id" | "status" | "status_msg" | "created_at" | "updated_at">
) => ({
  name: d.name,
  description: d.description,
  market: map.provider(d.market_provider, d.market_config),
  strategy: map.provider(d.strategy_provider, d.strategy_config),
  engine: map.provider(d.engine_provider, d.engine_config),
  symbol: d.symbol,
  timeframe: d.timeframe,
  startDate: d.start_date,
  endDate: d.end_date || undefined,
  enabled: d.enabled,
  metadata: d.metadata ? map.json(d.metadata) : undefined,
  recalculate: d.recalculate || 0,
  barMagnifier: d.bar_magnifier || "",
});
const previewInput = (d: PreviewInput) => ({
  market: map.provider(d.market_provider, d.market_config),
  strategy: map.provider(d.strategy_provider, d.strategy_config),
  engine: map.provider(d.engine_provider, d.engine_config),
  symbol: d.symbol,
  timeframe: d.timeframe,
  startDate: d.start_date,
  endDate: d.end_date || undefined,
  recalculate: d.recalculate || 0,
  recalculateSetting: d.recalculate_setting,
  barMagnifier: d.bar_magnifier || "",
  analyses: d.analyses || [],
});
const saveAccount = (row: Account) => {
  accounts.update((s) => ({ ...s, accounts: upsert(s.accounts, row) }));
  return row;
};
const saveScript = (row: Script) => {
  scripts.update((rows) => upsert(rows, row));
  return row;
};
const saveBacktest = (row: Backtest) => {
  backtests.update((rows) => upsert(rows, row));
  return row;
};
const configRpc = (category: string) =>
  ["account", "account_provider", "notification_channel"].includes(category) ? appRpc : previewRpc;
const configInput = (d: ConfigRequest) => ({
  category: map.categoryOf(d.category.replace("_provider", "")),
  name: d.name,
  config: Object.fromEntries(Object.entries(d.config || {}).map(([k, v]) => [k, String(v)])),
});
type LinkInput = {
  backtest_id: string;
  account_id: string;
  crypto_limit?: number | string | null;
  fiat_limit?: number | string | null;
  slippage?: number | string | null;
  symbol?: string;
  bypass?: boolean;
  reduce_only?: boolean;
};
const decimal = (v?: number | string | null) => (v == null || v === "" ? undefined : String(v));
const linkInput = (d: LinkInput) => ({
  backtestId: { value: d.backtest_id },
  accountId: { value: d.account_id },
  cryptoLimit: decimal(d.crypto_limit),
  fiatLimit: decimal(d.fiat_limit),
  slippage: decimal(d.slippage),
  symbol: d.symbol || undefined,
  bypass: d.bypass || false,
  reduceOnly: d.reduce_only || false,
});
export const commands = {
  async createAccount(
    d: Pick<Account, "name" | "description" | "provider" | "config"> & { enabled?: boolean }
  ) {
    return saveAccount(
      map.account(
        await appRpc.upsertAccount({ input: accountInput({ ...d, enabled: d.enabled ?? false }) })
      )
    );
  },
  async updateAccount(
    d: Partial<Pick<Account, "name" | "description" | "provider" | "config" | "enabled">> & {
      id: string;
    }
  ) {
    if (Object.keys(d).every((k) => k === "id" || k === "enabled") && d.enabled !== undefined)
      return commands.toggleAccount({ id: d.id, enabled: d.enabled });
    const current = map.account(await appRpc.getAccount({ value: d.id }));
    return saveAccount(
      map.account(
        await appRpc.upsertAccount({
          id: { value: d.id },
          input: accountInput({ ...current, ...d }),
        })
      )
    );
  },
  async toggleAccount(d: { id: string; enabled: boolean }) {
    return saveAccount(
      map.account(await appRpc.toggleAccount({ id: { value: d.id }, enabled: d.enabled }))
    );
  },
  async restartAccount(d: { id: string }) {
    await appRpc.restartAccount({ value: d.id });
  },
  async deleteAccount(d: { id: string }) {
    await appRpc.deleteAccount({ value: d.id });
    accounts.update((s) => ({ ...s, accounts: s.accounts.filter((r) => r.id !== d.id) }));
  },
  async listOrders(d: OrderScope = {}) {
    return (
      await appRpc.listOrders({
        accountId: d.account_id ? { value: d.account_id } : undefined,
        backtestId: d.backtest_id ? { value: d.backtest_id } : undefined,
        status: d.status || "",
        limit: d.limit || 200,
      })
    ).orders.map(map.order);
  },
  async getOrderStats(d: Pick<OrderScope, "account_id" | "backtest_id"> = {}) {
    return map.plain(
      await appRpc.getOrderStats({
        accountId: d.account_id ? { value: d.account_id } : undefined,
        backtestId: d.backtest_id ? { value: d.backtest_id } : undefined,
      })
    );
  },
  async createScript(d: Pick<Script, "name" | "description" | "source" | "language">) {
    return saveScript(map.plain(await appRpc.upsertScript({ input: d })) as Script);
  },
  async updateScript(
    d: Partial<Pick<Script, "name" | "description" | "source" | "language">> & { id: string }
  ) {
    const current = map.plain(await appRpc.getScript({ value: d.id })) as Script;
    return saveScript(
      map.plain(
        await appRpc.upsertScript({ id: { value: d.id }, input: { ...current, ...d } })
      ) as Script
    );
  },
  async listScriptRevisions(d: { id: string }) {
    return (await appRpc.listScriptRevisions({ scriptId: { value: d.id } })).revisions.map(
      map.plain
    );
  },
  async getScriptRevision(d: { id: string; rev_no: number }) {
    return map.plain(
      await appRpc.getScriptRevision({ scriptId: { value: d.id }, revNo: d.rev_no })
    );
  },
  async createBacktest(
    d: Omit<Parameters<typeof backtestInput>[0], "enabled"> & { enabled?: boolean }
  ) {
    return saveBacktest(
      map.backtest(
        await appRpc.upsertBacktest({ input: backtestInput({ ...d, enabled: d.enabled ?? false }) })
      )
    );
  },
  async updateBacktest(d: Partial<Parameters<typeof backtestInput>[0]> & { id: string }) {
    if (Object.keys(d).every((k) => k === "id" || k === "enabled") && d.enabled !== undefined)
      return commands.toggleBacktest({ id: d.id, enabled: d.enabled });
    const current = map.backtest(await appRpc.getBacktest({ value: d.id }));
    return saveBacktest(
      map.backtest(
        await appRpc.upsertBacktest({
          id: { value: d.id },
          input: backtestInput({ ...current, ...d }),
        })
      )
    );
  },
  async toggleBacktest(d: { id: string; enabled: boolean }) {
    return saveBacktest(
      map.backtest(await appRpc.toggleBacktest({ id: { value: d.id }, enabled: d.enabled }))
    );
  },
  async deleteBacktest(d: { id: string }) {
    await appRpc.deleteBacktest({ value: d.id });
    backtests.update((rows) => rows.filter((r) => r.id !== d.id));
  },
  async addBacktestAccount(d: LinkInput) {
    return saveBacktest(map.backtest(await appRpc.addBacktestAccount(linkInput(d))));
  },
  async updateBacktestAccount(d: LinkInput) {
    return saveBacktest(map.backtest(await appRpc.updateBacktestAccount(linkInput(d))));
  },
  async removeBacktestAccount(d: Pick<LinkInput, "account_id" | "backtest_id">) {
    return saveBacktest(map.backtest(await appRpc.removeBacktestAccount(linkInput(d))));
  },
  async listConfigurables() {
    const [app, preview] = await Promise.all([
      appRpc.getConfigurableList({}),
      previewRpc.getConfigurableList({}),
    ]);
    return {
      account_providers: app.accountProviders,
      notification_channels: app.notificationChannels,
      market_providers: preview.marketProviders,
      strategies: preview.strategies,
      engines: preview.engines,
    };
  },
  async listProviderInfos() {
    return map.providerInfos(
      await appRpc.getConfigurableProviders({}),
      await previewRpc.getConfigurableProviders({})
    );
  },
  async getParams(d: ConfigRequest) {
    return map.params(await configRpc(d.category).getConfigurableParams(configInput(d)));
  },
  async validateConfigurable(d: ConfigRequest) {
    const res = await configRpc(d.category).validateConfigurable(configInput(d));
    return { valid: res.valid, error: res.error };
  },
  async getStrategyInfo(d: { strategy_provider: string; strategy_config?: Config }) {
    return map.strategyInfo(
      await previewRpc.getStrategyInfo({
        strategy: map.provider(d.strategy_provider, d.strategy_config),
      })
    );
  },
  async getMarketSymbols(d: MarketRequest) {
    return (
      await previewRpc.getMarketSymbols(map.provider(d.market_provider, d.market_config))
    ).symbols.map(map.plain);
  },
  async getMarketTimeframes(d: MarketRequest) {
    return (await previewRpc.getMarketTimeframes(map.provider(d.market_provider, d.market_config)))
      .timeframes;
  },
  async previewBacktest(d: PreviewInput, timeoutMs = 600000, signal?: AbortSignal) {
    return map.result(await previewRpc.previewBacktest(previewInput(d), { timeoutMs, signal }));
  },
  async createPreviewStream(d: PreviewInput, timeoutMs = 600000, signal?: AbortSignal) {
    return previewRpc.createPreviewStream(previewInput(d), { timeoutMs, signal });
  },
};
export type Commands = typeof commands;
